import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type MessageReactionType = 'channel' | 'direct' | 'comment';

export interface Reaction {
  id: string;
  message_id: string;
  message_type: MessageReactionType;
  user_id: string;
  emoji: string;
  created_at: string;
  user?: {
    id: string;
    full_name: string | null;
  };
}

export interface GroupedReaction {
  emoji: string;
  count: number;
  users: { id: string; name: string }[];
  hasReacted: boolean;
}

export const QUICK_REACTIONS = ['👍🏽', '👎🏽', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👀', '✅'];

export const useMessageReactions = (
  messageId: string | undefined,
  messageType: MessageReactionType
) => {
  const queryClient = useQueryClient();

  const { data: reactions = [], isLoading } = useQuery({
    queryKey: ["message-reactions", messageId, messageType],
    queryFn: async () => {
      if (!messageId) return [];

      const { data, error } = await supabase
        .from("message_reactions")
        .select(`
          id,
          message_id,
          message_type,
          user_id,
          emoji,
          created_at
        `)
        .eq("message_id", messageId)
        .eq("message_type", messageType);

      if (error) throw error;

      // Fetch user profiles for reactions
      const userIds = [...new Set(data?.map(r => r.user_id) || [])];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      return data?.map(r => ({
        ...r,
        user: profileMap.get(r.user_id),
      })) as Reaction[];
    },
    enabled: !!messageId,
  });

  const { mutateAsync: addReaction } = useMutation({
    mutationFn: async (emoji: string) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user || !messageId) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("message_reactions")
        .insert({
          message_id: messageId,
          message_type: messageType,
          user_id: session.session.user.id,
          emoji,
        })
        .select()
        .single();

      if (error) {
        // If duplicate, ignore (user already reacted with this emoji)
        if (error.code === '23505') return null;
        throw error;
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-reactions", messageId, messageType] });
    },
    onError: (error: Error) => {
      console.error("Error adding reaction:", error);
    },
  });

  const { mutateAsync: removeReaction } = useMutation({
    mutationFn: async (emoji: string) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user || !messageId) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("message_reactions")
        .delete()
        .eq("message_id", messageId)
        .eq("message_type", messageType)
        .eq("user_id", session.session.user.id)
        .eq("emoji", emoji);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["message-reactions", messageId, messageType] });
    },
  });

  const toggleReaction = async (emoji: string, currentUserId: string) => {
    const existingReaction = reactions.find(
      r => r.emoji === emoji && r.user_id === currentUserId
    );

    if (existingReaction) {
      await removeReaction(emoji);
    } else {
      await addReaction(emoji);
    }
  };

  // Group reactions by emoji
  const groupedReactions = (currentUserId: string): GroupedReaction[] => {
    const groups = new Map<string, { users: { id: string; name: string }[] }>();

    reactions.forEach(r => {
      if (!groups.has(r.emoji)) {
        groups.set(r.emoji, { users: [] });
      }
      groups.get(r.emoji)!.users.push({
        id: r.user_id,
        name: r.user?.full_name || 'Unknown',
      });
    });

    return Array.from(groups.entries()).map(([emoji, data]) => ({
      emoji,
      count: data.users.length,
      users: data.users,
      hasReacted: data.users.some(u => u.id === currentUserId),
    }));
  };

  return {
    reactions,
    isLoading,
    addReaction,
    removeReaction,
    toggleReaction,
    groupedReactions,
  };
};
